<div class="shell">
  <aside class="sidebar" [class.is-collapsed]="collapsed()">
    <div class="brand">
      <div class="brand-id">
        <div class="brand-mark" data-testid="layout-brand-mark">
          <app-company-mark
            [logoUrl]="brandLogoUrl()"
            [name]="brandName()"
            (logoFailed)="authService.refreshCompany()"
          />
        </div>
        <div class="brand-text">
          <div class="brand-name" data-testid="layout-brand-name" [attr.title]="brandName()">
            {{ brandName() }}
          </div>
          <div class="brand-sub">Fleet command</div>
        </div>
      </div>

      <button
        data-testid="layout-collapse-btn"
        class="collapse-btn"
        nz-tooltip
        [nzTooltipTitle]="collapseLabel()"
        nzTooltipPlacement="right"
        [attr.aria-expanded]="!collapsed()"
        [attr.aria-label]="collapseLabel()"
        (click)="toggleCollapsed()"
      >
        <nz-icon [nzType]="collapsed() ? 'menu-unfold' : 'menu-fold'" />
      </button>
    </div>

    <nav class="nav">
      @for (section of sections; track section.label) {
        @if (sectionVisible(section)) {
          <div class="nav-section">
            <div class="nav-label">{{ section.label }}</div>
            @for (item of section.items; track item.label) {
              @if (canSee(item)) {
                @if (item.soon) {
                  <span
                    [attr.data-testid]="'nav-soon-' + item.label.toLowerCase().replaceAll(' ', '-')"
                    class="nav-item is-soon"
                    aria-disabled="true"
                    nz-tooltip
                    [nzTooltipTitle]="collapsed() ? item.label + ' — soon' : null"
                    nzTooltipPlacement="right"
                    [attr.aria-label]="collapsed() ? item.label + ' — soon' : null"
                  >
                    <nz-icon [nzType]="item.icon" class="ico" />
                    <span>{{ item.label }}</span>
                    <span class="soon">SOON</span>
                  </span>
                } @else {
                  <a
                    [attr.data-testid]="'nav-' + item.path!.replaceAll('/', '-')"
                    class="nav-item"
                    [routerLink]="item.path"
                    routerLinkActive="is-active"
                    nz-tooltip
                    [nzTooltipTitle]="collapsed() ? item.label : null"
                    nzTooltipPlacement="right"
                    [attr.aria-label]="collapsed() ? item.label : null"
                  >
                    <nz-icon [nzType]="item.icon" class="ico" />
                    <span>{{ item.label }}</span>
                    @if (badgeFor(item); as count) {
                      <span class="nav-badge" [attr.aria-label]="count + ' open'">{{
                        count > 99 ? '99+' : count
                      }}</span>
                    }
                  </a>
                }
              }
            }
          </div>
        }
      }
    </nav>
  </aside>

  <div class="main">
    <header class="topbar">
      <div class="topbar-text">
        <h1>{{ pageTitle() }}</h1>
      </div>

      <div class="spacer"></div>

      <button
        data-testid="header-theme-toggle"
        class="icon-btn"
        nz-tooltip
        [nzTooltipTitle]="theme.switchLabel()"
        [attr.aria-label]="theme.switchLabel()"
        (click)="theme.toggle()"
      >
        <nz-icon [nzType]="theme.mode() === 'dark' ? 'moon' : 'sun'" />
      </button>

      <button
        data-testid="header-notifications-btn"
        class="icon-btn"
        nz-dropdown
        [nzDropdownMenu]="notificationMenu"
        [nzVisible]="notifOpen()"
        nzTrigger="click"
        nzPlacement="bottomRight"
        (nzVisibleChange)="onNotifVisibleChange($event)"
        nz-tooltip
        [nzTooltipTitle]="bellLabel()"
        [attr.aria-label]="bellLabel()"
        aria-haspopup="menu"
      >
        <nz-badge
          class="notif-badge"
          [class.pulse]="badgePulse()"
          nzSize="small"
          [nzCount]="unreadCount()"
          [nzOverflowCount]="9"
          [nzOffset]="[6, -6]"
        >
          <nz-icon nzType="bell" />
        </nz-badge>
        @if (realtime.paused()) {
          <span class="paused-dot" aria-hidden="true"></span>
        }
      </button>

      <nz-dropdown-menu #notificationMenu="nzDropdownMenu">
        <app-notification-panel
          [isOpen]="notifOpen()"
          (unreadChanged)="refreshUnreadCount()"
          (closed)="notifOpen.set(false)"
        />
      </nz-dropdown-menu>

      <button
        data-testid="header-account-btn"
        class="avatar-btn"
        nz-dropdown
        [nzDropdownMenu]="accountMenu"
        nzTrigger="click"
        nzPlacement="bottomRight"
        aria-label="Account menu"
        aria-haspopup="menu"
      >
        @if (user()?.avatarUrl) {
          <nz-avatar
            class="avatar"
            [nzSrc]="user()?.avatarUrl"
            data-testid="header-profile-avatar"
          ></nz-avatar>
        } @else {
          <nz-avatar
            class="avatar"
            [nzText]="getInitialName()"
            data-testid="header-profile-avatar"
          ></nz-avatar>
        }
        <nz-icon nzType="down" class="avatar-caret" />
      </button>

      <nz-dropdown-menu #accountMenu="nzDropdownMenu">
        <ul nz-menu class="account-menu">
          <li class="account-head">
            <span class="account-name">{{ fullName() }}</span>
            <span class="account-mail">{{ user()?.email }}</span>
          </li>
          <li nz-menu-divider></li>
          <li data-testid="header-menu-profile" nz-menu-item routerLink="/profile">
            <nz-icon nzType="user" class="mi-ico" />
            <span>Profile</span>
          </li>
          <li
            data-testid="header-menu-logout"
            nz-menu-item
            tabindex="0"
            (click)="logout()"
            (keydown.enter)="logout()"
          >
            <nz-icon nzType="logout" class="mi-ico" />
            <span>Log out</span>
          </li>
        </ul>
      </nz-dropdown-menu>
    </header>

    @if (authService.passwordUpgradeRecommended()) {
      <nz-alert
        class="block"
        nzType="warning"
        nzBanner
        nzShowIcon
        nzCloseable
        [nzMessage]="passwordNudge"
        (nzOnClose)="authService.dismissPasswordUpgrade()"
      />
      <ng-template #passwordNudge>
        Passwords now need at least {{ minPasswordLength }} characters. Yours is shorter.
        <a data-testid="layout-change-password-link" routerLink="/profile">Change password</a>
      </ng-template>
    }

    <main class="content">
      <router-outlet></router-outlet>
    </main>
  </div>
</div>
